import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { LOCALE_CONFIG } from "@/config/locale-domains"
import { getSeoKey, shouldNoindex } from "@/config/seo-routes"
import { useBrand } from "@/hooks/use-brand"

function upsertMeta(attr, key, content) {
  if (!content) return
  let el = document.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute("content", content)
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return
  const selector = Object.entries(extra).reduce(
    (s, [k, v]) => `${s}[${k}="${v}"]`,
    `link[rel="${rel}"]`
  )
  let el = document.querySelector(selector)
  if (!el) {
    el = document.createElement("link")
    el.setAttribute("rel", rel)
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v))
    document.head.appendChild(el)
  }
  el.setAttribute("href", href)
}

function removeHreflangLinks() {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())
}

export function PageSEO({ seoKey: seoKeyOverride, title, description, image, noindex, jsonLd }) {
  const { pathname } = useLocation()
  const { t, i18n } = useTranslation()
  const brand = useBrand()
  const seoKey = seoKeyOverride || getSeoKey(pathname)
  const locale = i18n.language?.startsWith("hu") ? "hu" : "ro"
  const roDomain = LOCALE_CONFIG.ro.domain
  const huDomain = LOCALE_CONFIG.hu.domain
  const currentDomain = locale === "hu" ? huDomain : roDomain
  const canonicalUrl = `https://${currentDomain}${pathname}`
  const ogImage = image || `${process.env.PUBLIC_URL || ""}/og-default.svg`

  const pageTitle =
    title || t(`seo.${seoKey}.title`, { brand: brand.brand, defaultValue: t("meta.title", { brand: brand.brand }) })
  const pageDescription =
    description ||
    t(`seo.${seoKey}.description`, { brand: brand.brand, defaultValue: t("meta.description") })

  const isNoindex = noindex ?? shouldNoindex(pathname)

  useEffect(() => {
    document.documentElement.lang = brand.htmlLang
    document.title = pageTitle

    upsertMeta("name", "description", pageDescription)
    upsertMeta("name", "robots", isNoindex ? "noindex, nofollow" : "index, follow")

    upsertLink("canonical", canonicalUrl)

    removeHreflangLinks()
    ;[
      ["ro", `https://${roDomain}${pathname}`],
      ["hu", `https://${huDomain}${pathname}`],
      ["x-default", `https://${roDomain}${pathname}`],
    ].forEach(([hreflang, href]) => {
      const link = document.createElement("link")
      link.setAttribute("rel", "alternate")
      link.setAttribute("hreflang", hreflang)
      link.setAttribute("href", href)
      document.head.appendChild(link)
    })

    upsertMeta("property", "og:title", pageTitle)
    upsertMeta("property", "og:description", pageDescription)
    upsertMeta("property", "og:url", canonicalUrl)
    upsertMeta("property", "og:type", pathname.startsWith("/blog/") ? "article" : "website")
    upsertMeta("property", "og:locale", locale === "hu" ? "hu_HU" : "ro_RO")
    upsertMeta("property", "og:image", ogImage.startsWith("http") ? ogImage : `https://${currentDomain}${ogImage}`)

    upsertMeta("name", "twitter:card", "summary_large_image")
    upsertMeta("name", "twitter:title", pageTitle)
    upsertMeta("name", "twitter:description", pageDescription)
    upsertMeta("name", "twitter:image", ogImage.startsWith("http") ? ogImage : `https://${currentDomain}${ogImage}`)
  }, [
    pageTitle,
    pageDescription,
    canonicalUrl,
    brand.htmlLang,
    pathname,
    locale,
    roDomain,
    huDomain,
    isNoindex,
    ogImage,
  ])

  useEffect(() => {
    if (!jsonLd) return
    const id = "page-json-ld"
    let script = document.getElementById(id)
    if (!script) {
      script = document.createElement("script")
      script.id = id
      script.type = "application/ld+json"
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(jsonLd)
    return () => {
      script?.remove()
    }
  }, [jsonLd])

  return null
}
